import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {Box, CircularProgress, Link, Typography} from '@mui/material';
import {NoAuthLayout} from 'src/components/MainLayout/NoAuthLayout';
import {DtxSpacer} from 'src/components/common/DtxSpacer';
import {DefaultButton} from 'src/components/common/DefaultButton';
import {MicrosoftButton} from 'src/components/common/MicrosoftButton';
import {useAuth} from 'src/auth/context';
import {useSignInOptions, type SignInOption} from 'src/auth/signInOptions';
import {IdpError} from 'src/auth/types';
import {dashboardRoute} from 'src/routes';

const documentTitle = 'Sign in - Detexian';

const SupportLink = () =>
  <Link href="mailto:support@detexian.com">support@detexian.com</Link>;

/** Where to land after signing in, from the route that bounced us here. */
const useReturnTo = (): string => {
  const location = useLocation();
  const state = location.state as {returnTo?: unknown} | null;
  return typeof state?.returnTo === 'string' ? state.returnTo : dashboardRoute;
};

/**
 * The sign-in page. Every sign-in is federated through the IdP: one button per
 * configured provider, each handing off to that provider and returning to
 * AuthCallback.
 */
export const LoginPage = () => {
  document.title = documentTitle;

  const navigate = useNavigate();
  const returnTo = useReturnTo();
  const {startFederatedLogin, isAuthenticated} = useAuth();
  const {options, isLoading: optionsLoading} = useSignInOptions();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const noOptions = !optionsLoading && options.length === 0;

  // A session restored while the page was mounting makes signing in moot.
  useEffect(() => {
    if (isAuthenticated) navigate(returnTo, {replace: true});
  }, [isAuthenticated, navigate, returnTo]);

  const start = async (option: SignInOption) => {
    setBusy(true);
    setError(undefined);

    try {
      await startFederatedLogin(option.provider, {
        ...(option.connectionId ? {connectionId: option.connectionId} : {}),
        returnTo,
      });
      // The browser is now navigating away; nothing further to do here.
    } catch (cause) {
      if (cause instanceof IdpError) {
        setError(cause.message);
      } else {
        console.error(cause);
        setError('Something went wrong. Try again.');
      }
      setBusy(false);
    }
  };

  return (
    <NoAuthLayout>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        textAlign: 'center',
        maxWidth: '360px',
      }}>
        <DtxSpacer space={2}/>
        {optionsLoading && <CircularProgress size={24}/>}

        {options.map(option => {
          const key = `${option.provider}:${option.connectionId ?? 'platform'}`;
          return option.provider === 'microsoft'
            ? <MicrosoftButton
                key={key}
                width="260px"
                title="Sign in with Microsoft"
                disabled={busy}
                onClick={() => void start(option)}
              />
            : <DefaultButton
                key={key}
                disabled={busy}
                onClick={() => void start(option)}
              >
                {`Continue with ${option.label}`}
              </DefaultButton>;
        })}

        {noOptions &&
          <Typography role="alert" color="error">
            No sign-in provider is available. Email <SupportLink/> and we will get you in.
          </Typography>
        }

        {error &&
          <Typography role="alert" color="error">{error}</Typography>
        }

        {!noOptions &&
          <Typography color="text.secondary">
            Trouble signing in? Email <SupportLink/>.
          </Typography>
        }
        <DtxSpacer space={2}/>
      </Box>
    </NoAuthLayout>
  );
};
